import api from './api';

export interface Contractor {
  id: string;
  name: string;
  address: string;
  location: string;
  nip: string;
  industry: string;
  email: string;
  email2?: string;
  phone: string;
  rating: string;
  type?: string;
}

export interface ContractorFilter {
  searchText?: string;
  location?: string;
  nipText?: string;
  industry?: string;
  type?: string;
}

export interface ContractorResponse {
  contractors: Contractor[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

export interface MssqlUpdateResult {
  stats: {
    created: number;
    updated: number;
    errors: number;
  };
  message: string;
}

const contractorService = {
  getAllContractors: async (page: number = 1, limit: number = 10): Promise<ContractorResponse> => {
    try {
      const response = await api.get(`/contractors?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania kontrahentów:', error);
      throw error;
    }
  },

  getContractorById: async (id: string): Promise<Contractor> => {
    try {
      const response = await api.get(`/contractors/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Błąd podczas pobierania kontrahenta o ID ${id}:`, error);
      throw error;
    }
  },

  createContractor: async (contractorData: Omit<Contractor, 'id'>): Promise<Contractor> => {
    try {
      const response = await api.post('/contractors', contractorData);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas tworzenia kontrahenta:', error);
      throw error;
    }
  },

  updateContractor: async (id: string, contractorData: Partial<Contractor>): Promise<Contractor> => {
    try {
      const response = await api.put(`/contractors/${id}`, contractorData);
      return response.data;
    } catch (error) {
      console.error(`Błąd podczas aktualizacji kontrahenta o ID ${id}:`, error);
      throw error;
    }
  },

  deleteContractor: async (id: string): Promise<void> => {
    try {
      await api.delete(`/contractors/${id}`);
    } catch (error) {
      console.error(`Błąd podczas usuwania kontrahenta o ID ${id}:`, error);
      throw error;
    }
  },

  filterContractors: async (
    filters: ContractorFilter,
    page: number = 1,
    limit: number = 10
  ): Promise<ContractorResponse> => {
    try {
      // Budowanie parametrów zapytania
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('limit', limit.toString());

      if (filters.searchText) {
        queryParams.append('searchText', filters.searchText);
      }

      if (filters.location) {
        queryParams.append('location', filters.location);
      }

      if (filters.nipText) {
        queryParams.append('nipText', filters.nipText);
      }

      if (filters.industry) {
        queryParams.append('industry', filters.industry);
      }

      if (filters.type) {
        queryParams.append('type', filters.type);
      }

      const response = await api.get(`/contractors/filter?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas filtrowania kontrahentów:', error);
      throw error;
    }
  },

  updateContractorsFromMssql: async (): Promise<MssqlUpdateResult> => {
    try {
      const response = await api.post('/contractors/update-from-mssql');
      return response.data;
    } catch (error) {
      console.error('Błąd podczas aktualizacji kontrahentów z MSSQL:', error);
      throw error;
    }
  }
};

export default contractorService;
